import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { useAuth } from '../contexts/AuthContext';
import { isSalesRole } from '../lib/sales-access';
import { getRoleLabel } from '../lib/roles';
import { SuppressedRecipientNotice, suppressedRefusalOf, type SuppressedRefusal } from '../components/SuppressedRecipientNotice';
import { formatCalendarDate, formatInstant, formatInstantDay, formatInstantTenantDay, parseCalendarDate, todayCalendarDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { fmtCurrency, fmtUnitRate, parseMoney, fmtUnit, fmtLineItemType } from '../lib/display-labels';
import { ndtQuoteCurrency, ndtQuoteLineKind } from '../lib/ndt-quotes';
import { PF_QUOTE_VERTICAL, pfQuoteCurrency, pfQuoteLineKind } from '../lib/pf-quotes';
import { canAdminPf } from '../lib/pipeline-facility';
import { CreatePfJobAction } from '../components/quotes/CreatePfJobAction';
import { WT_QUOTE_VERTICAL, wtQuoteCurrency, wtQuoteLineKind } from '../lib/wt-quotes';
import { canAdminWt } from '../lib/well-testing';
import { CreateWtJobAction } from '../components/quotes/CreateWtJobAction';
import { EW_QUOTE_VERTICAL, ewQuoteCurrency, ewQuoteLineKind } from '../lib/ew-quotes';
import { canAdminEw } from '../lib/earthworks';
import { CreateEwJobAction } from '../components/quotes/CreateEwJobAction';
import { WS_QUOTE_VERTICAL, wsQuoteCurrency, wsQuoteLineKind } from '../lib/ws-quotes';
import { canAdminWs } from '../lib/well-servicing';
import { CreateWsJobAction } from '../components/quotes/CreateWsJobAction';
import { EwQuotePanel } from '../components/quotes/EwQuotePanel';
import { humanizeLabel } from '../lib/reports';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import ConfirmModal from '../components/ui/ConfirmModal';
import { QuoteCreatedRecordsPanel } from '../components/quotes/QuoteCreatedRecordsPanel';
import { QuoteToolTermsPanel, type QuoteToolTerm } from '../components/quotes/QuoteToolTermsPanel';
import { QuoteCrewBindingPanel, isCrewLine } from '../components/quotes/QuoteCrewBindingPanel';
import type { DownholeQuoteBinding, QuoteCreatedRecords } from '../types/quote-pricing';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../lib/load-failure';
import {
  FileText,
  Send,
  Download,
  Pencil,
  CheckCircle,
  XCircle,
  X,
  ChevronDown,
  ChevronUp,
  Loader2,
  Clock,
  User,
  Users,
  Calendar,
  Receipt,
  DollarSign,
  AlertTriangle,
  ArrowRightLeft,
  ExternalLink,
} from 'lucide-react';
import { Tooltip } from '../components/ui/tooltip';

// ── Types ────────────────────────────────────────────────────────────────────

interface LineItem {
  id: string;
  line_item_type?: string;
  description?: string;
  quantity: number;
  unit?: string;
  rate: number;
  total: number;
  is_discount?: boolean;
  /** Which rate store priced the line (api 0471); 'dh_personnel_rate' is a DD/MWD crew line (api#1092). */
  source_kind?: string | null;
}

/**
 * ONE AUDIT ROW, TYPED TO THE PAYLOAD THE API ACTUALLY SENDS.
 *
 * `GET /quotes/:id/audit-log` hands back the raw joined row, with the user
 * split across three columns — `first_name`, `last_name`, `email`. `user_name`
 * has never been a key on it, so no row on this panel has ever named who did
 * the thing. Verified against a live response, not assumed.
 *
 * `user_name` is kept as a fallback rather than deleted (reads tolerate both),
 * but it is the second rung now, not the only one.
 */
interface AuditEntry {
  id: string;
  action: string;
  description?: string;
  created_at?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  user_name?: string;
}

/** Who did it, or null — never a guess, and never the empty string. */
function auditActor(entry: AuditEntry): string | null {
  const name = [entry.first_name, entry.last_name].filter(Boolean).join(' ').trim();
  return name || entry.user_name || entry.email || null;
}

interface Quote {
  id: string;
  quote_number: string;
  status: string;
  /** The quote's customer (quotes.customer_id). The Pipeline job action lists this customer's sites. */
  customer_id?: string | null;
  customer_name?: string;
  customer_display_name?: string;
  customer_contact_email?: string;
  /**
   * WHICH `customers` column supplied the address — API 0392. Snake_case,
   * because this endpoint hands back the raw row; see CLAUDE.md on matching
   * the surface you are on rather than half-normalising.
   *
   * Null renders as UNKNOWN. Never fall back to naming a column.
   */
  customer_contact_email_source?: 'accounting_email' | 'main_contact_email' | null;
  customer_address?: string;
  customer_city?: string;
  customer_province_state?: string;
  subtotal: number;
  discount_total?: number;
  tax_rate_1_label?: string;
  tax_rate_1_percent?: number;
  tax_rate_1_amount?: number;
  tax_rate_2_label?: string;
  tax_rate_2_percent?: number;
  tax_rate_2_amount?: number;
  total_amount: number;
  notes?: string;
  quote_date?: string;
  valid_until?: string;
  created_at: string;
  sent_at?: string;
  accepted_at?: string;
  declined_at?: string;
  converted_invoice_id?: string;
  converted_at?: string;
  /** api 0471: the vertical whose rates priced the quote. Absent on an old payload means casing. */
  vertical?: string;
  line_items: LineItem[];
  /** api 0488 — downhole only: minimum charge and lost in hole per tool, outside the total. */
  tool_terms?: QuoteToolTerm[];
  currency?: string;
}

// ── Downhole accept (api#956, #1092) ────────────────────────────────────────

/**
 * The accept question for a downhole quote, from the kinds of line it holds.
 * A crew-only quote builds no assembly (api#1092), so the question must not
 * promise one. A line with no `source_kind` (an older payload) is counted as a
 * tool line, which is what every downhole line was before crew existed.
 */
type PendingQuoteAction = { kind: 'accept' | 'decline' | 'convert'; body: string };

const PENDING_ACTION_COPY: Record<PendingQuoteAction['kind'], { title: string; confirmLabel: string; variant: 'primary' | 'destructive' }> = {
  accept: { title: 'Accept quote', confirmLabel: 'Accept quote', variant: 'primary' },
  decline: { title: 'Decline quote', confirmLabel: 'Decline quote', variant: 'destructive' },
  convert: { title: 'Convert to invoice', confirmLabel: 'Convert to invoice', variant: 'primary' },
};

export function downholeAcceptQuestion(lines: Array<{ source_kind?: string | null; is_discount?: boolean }>): string {
  const priced = lines.filter((l) => !l.is_discount);
  const crew = priced.some(isCrewLine);
  const tools = priced.some((l) => !isCrewLine(l));
  const parts = [
    tools ? 'sets this customer\'s tool rates to the quoted prices and opens an assembly' : null,
    crew ? 'sets this customer\'s crew rates, one per role, to the quoted prices' : null,
  ].filter(Boolean);
  return parts.length === 0
    ? 'Accept this quote?'
    : `Accept this quote? It ${parts.join(', and ')}, for the quote's validity window.`;
}

/** What the accept response says the binding wrote. Assembly is null on a crew-only quote. */
export function acceptedToast(b: DownholeQuoteBinding | undefined): string {
  if (!b) return 'Quote marked as accepted';
  const parts = [
    b.rates.length > 0 ? `${b.rates.length} customer tool rate${b.rates.length === 1 ? '' : 's'} set` : null,
    b.assembly ? `assembly ${b.assembly.assemblyNumber} opened` : null,
    b.personnelRates && b.personnelRates.length > 0
      ? `${b.personnelRates.length} customer crew rate${b.personnelRates.length === 1 ? '' : 's'} set`
      : null,
  ].filter(Boolean);
  return parts.length ? `Quote accepted — ${parts.join(', ')}` : 'Quote accepted';
}

// ── Status badge ─────────────────────────────────────────────────────────────

/**
 * THE STATUS BADGE — saturated fill, dark ink, and an em dash for anything
 * outside the map. Same shape as TicketDetail's (fe#734) and the invoice
 * detail's; the labels are this document's own.
 *
 *   slate   nothing is in play — draft, expired
 *   blue    out with the customer, waiting — sent
 *   green   won — accepted
 *   red     lost — declined
 *
 * `text-fi-signal-ink` on a `bg-fi-signal-*` fill: the signal hues are declared
 * once and do not follow the theme, so one dark ink holds on all of them in
 * both halves. This replaced a pale-wash-plus-bright-ink shape whose every
 * spelling was a raw Tailwind shade — a 400-weight ink on a white panel in
 * light mode. (Described rather than spelled: the Tailwind scanner reads a
 * comment as source, so naming the retired classes would mint them again.)
 *
 * A VALUE OUTSIDE THIS MAP READS `—` ON SLATE. The old map fell through to a
 * grey pill carrying the RAW status string.
 */
const STATUS_BADGE: Record<string, { label: string; fill: string }> = {
  draft:    { label: 'Draft',    fill: 'bg-fi-signal-slate' },
  sent:     { label: 'Sent',     fill: 'bg-fi-signal-blue' },
  accepted: { label: 'Accepted', fill: 'bg-fi-signal-green' },
  declined: { label: 'Declined', fill: 'bg-fi-signal-red' },
  expired:  { label: 'Expired',  fill: 'bg-fi-signal-slate' },
};

const UNKNOWN_STATUS = { label: '\u2014', fill: 'bg-fi-signal-slate' };

const statusSkin = (status: string | null | undefined) =>
  (status && STATUS_BADGE[status]) || UNKNOWN_STATUS;

const STATUS_PILL =
  'inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink';

/**
 * PAST ITS VALID-UNTIL — DERIVED HERE, BECAUSE NOTHING ELSE DERIVES IT.
 *
 * WHAT THE API DOES WITH AN EXPIRED QUOTE, checked before this was written
 * rather than assumed:
 *
 *   - `POST /quotes/mark-expired` sets `status = 'expired'` for every quote
 *     that is `sent` AND `valid_until < NOW()`. It is the ONLY writer of that
 *     status.
 *   - It has no caller. `api.quotes.markExpired` exists in client.ts with zero
 *     call sites, and nothing in fieldiq-api schedules it — no cron, no job, no
 *     other route invokes it. Grepped in both repos, 2026-09-22.
 *   - `POST /quotes/:id/send` does not look at `valid_until` at all. It will
 *     send a quote that lapsed a year ago, and stamp it `sent`.
 *
 * So a quote's own expiry date passes and NOTHING happens: the row still reads
 * Draft or Sent, and the send door is still open. The status column is not
 * wrong — it is simply not being maintained — so this does not overwrite it.
 * It renders a SECOND, clearly-labelled fact beside it, and the send doors
 * refuse with the reason.
 *
 * THIS IS NOT A SECOND ANSWER TO A QUESTION THE API ANSWERS (non-negotiable 5).
 * The API answers "what is this quote's status" and that badge still shows it.
 * This answers "is today past the date printed on the document", which no
 * endpoint answers for anyone.
 *
 * `valid_until` is a CALENDAR DAY, so the comparison is between two calendar
 * days and never `new Date()`: `new Date('2026-09-22')` is UTC midnight, which
 * in Alberta is 18:00 on the 21st, and a quote would read expired on its own
 * last valid day. Strictly BEFORE today — a quote valid until today is valid
 * today.
 */
function isPastValidUntil(validUntil: string | null | undefined, timeZone: string | null): boolean {
  if (!validUntil) return false;
  const day = parseCalendarDate(validUntil);
  if (!day) return false;
  // The TENANT's today. No zone, no claim that the quote has lapsed.
  const today = parseCalendarDate(todayCalendarDate(timeZone));
  return !!today && day.getTime() < today.getTime();
}

/**
 * WHY A SEND IS REFUSED, or undefined when it is not. ONE function, so the
 * three send buttons cannot disagree about it — two gates drift, and a
 * document refused by one and passed by the other is worse than either.
 *
 * Two reasons, and the lapse is reported first because it is the one the
 * office has to act on somewhere else (the editor) rather than in the field
 * beside the button.
 *
 * The no-recipient rule is the API's own: `POST /quotes/:id/send` resolves
 * `body.email || quote.customer_contact_email` and answers 400 "No recipient
 * email…" when both are empty. An empty FIELD on a quote that HAS an address
 * is a legal send — it posts no override and the document's own address is
 * used — so only the both-empty case refuses.
 */
function sendBlockReason(
  lapsedReason: string | undefined,
  typedAddress: string,
  documentAddress: string | null | undefined,
): string | undefined {
  if (lapsedReason) return lapsedReason;
  if (!typedAddress.trim() && !(documentAddress || '').trim()) {
    return 'This quote has no recipient. Type an address here, or set one on the customer record.';
  }
  return undefined;
}

// ── Body action skins ────────────────────────────────────────────────────────

/**
 * THE ACTIONS SIT IN THE BODY, NOT ON THE BAND, so these are spelled in
 * THEME-FOLLOWING ink tokens — the opposite of the band's constant white
 * opacities. `text-fieldiq-text` and `text-fieldiq-accent` are both remapped
 * under the light attribute (the accent to amber-700, 5.02 on the white card);
 * the raw 400- and 600-weight shades these replaced are remapped by nothing.
 *
 * AND THE HOVER IS THE ACCENT, NEVER A WHITE-INK HOVER: theme.css rewrites the
 * exact white-ink selector under the light attribute and deliberately does not
 * rewrite its hover variant, so the control vanishes while being pointed at.
 * (The retired class is described rather than spelled, for the scanner's sake.)
 */
const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50 disabled:cursor-not-allowed';
const ACTION_SECONDARY =
  'fi-action fi-action--standard rounded-fi-control border border-fieldiq-border bg-fieldiq-card text-fieldiq-text transition-colors hover:text-fieldiq-accent disabled:opacity-50';
const ACTION_DANGER =
  'fi-action fi-action--standard rounded-fi-control border border-fieldiq-danger/40 bg-fieldiq-card text-fieldiq-text transition-colors hover:bg-fieldiq-danger/10 disabled:opacity-50';

// ── Formatters ───────────────────────────────────────────────────────────────

/**
 * Money on this document renders its sign OUTSIDE the symbol — '-$42.00', not
 * fmtCurrency's own '$-42.00'. That is the only local behaviour here, so only
 * that is local: the absent/real-zero decision and the digits come from the
 * shared helper. The body used to take `value: number` and format it
 * unconditionally, so an absent amount arrived as NaN and rendered '$NaN'.
 */
// `currency` is passed for an NDT quote only (ndtQuoteCurrency); every other
// vertical passes null and keeps the bare '$' it always had.
function formatMoney(value: any, currency: string | null = null): string {
  const n = parseMoney(value);
  return n === null ? '\u2014' : fmtCurrency(Math.abs(n), currency);
}

/** A line's unit rate: as formatMoney, with the decimals the rate carries (fmtUnitRate). */
function formatRate(value: any, currency: string | null = null): string {
  const n = parseMoney(value);
  return n === null ? '\u2014' : fmtUnitRate(Math.abs(n), currency);
}

function formatMoneyWithSign(value: any, currency: string | null = null): string {
  const n = parseMoney(value);
  if (n === null) return '\u2014';
  return n < 0 ? `-${fmtCurrency(Math.abs(n), currency)}` : fmtCurrency(n, currency);
}

/**
 * The DAY an INSTANT falls on — `sent_at`, `accepted_at`, `created_at`. Not
 * `formatDay` below: an acceptance at 21:30 local on Jul 14 is stored
 * '2026-07-15T03:30:00Z', and only converting to local time answers Jul 14.
 *
 * THE SHARED HELPER, not a local one. This was a hand-rolled `new Date()` plus
 * `toLocaleDateString`, which is the shape `no-shadow-formatter` exists to
 * catch — and it was wrong in the way the rule predicts: a stamp in the
 * Postgres shape ('2026-09-04 00:56:18.511471+00') parses in V8 and is REJECTED
 * by JavaScriptCore, so the four dates on this panel would have degraded to
 * nothing on the iOS build alone. `formatInstantDay` now normalises the shape
 * (fixed in calendar-date.ts in this change, for all ~20 of its callers).
 *
 * Absent or unparseable is an EM DASH; it returned the two-hyphen string.
 */
const formatDate = (dateStr: string | null | undefined, timeZone: string | null): string =>
  formatInstantDay(dateStr, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '\u2014';

/**
 * `valid_until` is a CALENDAR DAY; the `_at` columns above are not, and
 * neither is `quote_date` — an instant (api#838), rendered further down as the
 * tenant's day through formatInstantTenantDay. Absent is an em dash — it was
 * the two-hyphen string, which is Quotes' own written deviation in CLAUDE.md,
 * not a pattern to carry forward.
 */
function formatDay(dateStr: string | null | undefined): string {
  return formatCalendarDate(dateStr, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '\u2014';
}

// ── The band, and the panel the body is built from ──────────────────────────

/**
 * THE BAND, IN ONE PLACE, because this page has four states and every one of
 * them has to wear it.
 *
 * The eyebrow is constant and the title is the quote's own identity. It carries
 * the back arrow (drawn by the band, no visible word) and NO description: a
 * description line has never been measured against the hero circle's clearance.
 *
 * Status, customer and the derived expiry chip sit in an identity row in the
 * BODY, directly under the band — where TicketDetail (fe#734) puts them. The
 * band's only free slots are a description line and the stat strip, and the
 * circle's clearance was swept against stat TEXT only.
 *
 * `stats` is OMITTED — not zeroed — while the quote has not arrived.
 */
function QuoteBand({
  title,
  onBack,
  stats,
}: {
  title: string;
  onBack: () => void;
  stats?: React.ComponentProps<typeof PageHeaderBand>['stats'];
}) {
  return (
    <PageHeaderBand
      eyebrow="Commercial pipeline"
      title={title}
      onBack={onBack}
      backLabel="Back to Quotes"
      stats={stats}
    />
  );
}

/**
 * A record panel, in the register idiom: one amber 4px left edge on the PANEL,
 * an eyebrow and an `<h2>`, and the rows inside it.
 */
function RecordPanel({
  eyebrow,
  title,
  count,
  children,
}: {
  eyebrow: string;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
      {/* A div, not a <header>: inside a <section> the element still maps to
          the banner role in some accessibility trees. */}
      <div className="border-b border-fieldiq-border px-4 py-3">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">
          {title}
          {count !== undefined && <span className="ml-2 font-bold text-fieldiq-muted">{count}</span>}
        </h2>
      </div>
      {children}
    </section>
  );
}

// ── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      role="status"
      className="fixed bottom-[var(--fab-bottom)] left-1/2 z-50 flex -translate-x-1/2 animate-fade-in items-center gap-2 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-5 py-3 text-sm text-white shadow-lg"
    >
      {message}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function QuoteDetailScreen() {
  const timeZone = useTenantTimeZone();
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Read to name the caller's role in a refusal notice, and to keep a sales
  // user read-only: they get the PDF and nothing that writes (api#1032).
  const { user } = useAuth();
  const isSales = isSalesRole(user?.role);

  const [toast, setToast] = useState<string | null>(null);
  /**
   * A 409 `recipient_suppressed` from the send door — see InvoiceDetailScreen.
   *
   * IT REMEMBERS WHICH ADDRESS WAS REFUSED, the way the invoice screen's does,
   * and that is not cosmetic. The forced retry read the LIVE input instead, and
   * the input is pre-filled with the quote's own address — so once the send
   * field opens, forcing posts that address as an explicit BODY override even
   * though the office never typed it. The server then treats the send as an
   * override and a later refusal can say nothing about WHICH customer column
   * the dead address came from, because a body address is nobody's field. The
   * whole provenance line becomes unreachable, silently, on the one flow whose
   * job is to tell you where a dead address lives.
   *
   * `email: undefined` is therefore MEANINGFUL: it says "no override was sent,
   * use the document's own". It is not the same as the document's address
   * spelled out, and the two must not collapse into one value.
   */
  const [suppressed, setSuppressed] = useState<{ refusal: SuppressedRefusal; email?: string } | null>(null);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [sendEmailAddress, setSendEmailAddress] = useState('');
  const [auditOpen, setAuditOpen] = useState(false);
  // Accepted surface-rentals quote → a draft rental order (api API-2b).
  const [rentalStart, setRentalStart] = useState('');
  // Accepted PT quote → a scheduled PT job; the day is optional (api#959).
  const [ptDay, setPtDay] = useState('');
  /**
   * A refusal from an action door, shown AS THE API WROTE IT — its sentence
   * and its code — and kept on screen until the next action, not in a toast
   * that disappears in three seconds. The downhole accept and the Create job
   * doors refuse with reasons the office has to act on (an overlapping tool
   * rate, a quote priced from no rate sheet); rewording them here would put a
   * second, drifting copy of each rule in the frontend.
   */
  const [refusal, setRefusal] = useState<{ code?: string; error: string } | null>(null);

  // ── Fetch quote ────────────────────────────────────────────────────────

  const quoteQuery = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: async () => {
      const res = await api.quotes.get(quoteId!);
      // `as Quote` on the old expression typed the ENVELOPE as a Quote whenever
      // data was null — every field then read undefined behind a Quote type.
      return unwrap<Quote>(res.data) ?? null;
    },
    enabled: !!quoteId,
    // Retry anything worth retrying and NOT a refusal, and keep the error where
    // the screen can find it. Without this a 500 leaves the query `paused` with
    // `error: null`, `data: undefined` and `isLoading: false` — which the old
    // `isError || !quote` branch read as "Quote not found", so a dropped
    // connection and a 403 both rendered as a fact about the row.
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const quote = quoteQuery.data;
  const isLoading = quoteQuery.isLoading;
  // Read off the QUERY, not off `error` alone: a query between retries carries
  // its failure in `failureReason` and nowhere else.
  const quoteFailure = queryLoadFailure(quoteQuery);

  // ── Fetch audit log (lazy) ─────────────────────────────────────────────

  const { data: auditLog, isLoading: auditLoading } = useQuery({
    queryKey: ['quote', quoteId, 'audit-log'],
    queryFn: async () => {
      const res = await api.quotes.auditLog(quoteId!);
      return (res.data?.data ?? res.data) as AuditEntry[];
    },
    // The audit log and created records are refused to sales (api#1032).
    enabled: !!quoteId && auditOpen && !isSales,
  });

  // ── What the accepted quote created (api GET /quotes/:id/created-records) ─

  const createdQuery = useQuery({
    queryKey: ['quote', quoteId, 'created-records'],
    queryFn: async () => unwrap<QuoteCreatedRecords>((await api.quotes.createdRecords(quoteId!)).data) ?? null,
    enabled: !!quoteId && quoteQuery.data?.status === 'accepted' && !isSales,
    retry: false,
  });
  const created = createdQuery.data ?? null;

  /** The API's own words for a refusal; a fallback only when it sent none (a dropped connection). */
  const refuse = (err: any, fallback: string) => {
    const body = err?.response?.data;
    setRefusal({ code: body?.code, error: body?.error || fallback });
  };
  const afterAction = () => {
    queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
    // A send mints an Earthworks quote's customer link; an accept decides it.
    if (quoteId) queryClient.invalidateQueries({ queryKey: ['earthworks', 'quote-state', quoteId] });
    setRefusal(null);
  };

  // ── Mutations ──────────────────────────────────────────────────────────

  const sendMutation = useMutation({
    // `email` was never passed from this screen, at either send site, so the
    // API's body override was unreachable here — the same hole the invoice
    // screen had, without even the absent-address branch to reach it through.
    mutationFn: (data: { email?: string; force?: boolean }) => api.quotes.send(quoteId!, data),
    onSuccess: () => {
      afterAction();
      setSuppressed(null);
      setToast('Quote sent successfully');
    },
    onError: (err: any, data) => {
      const refusal = suppressedRefusalOf(err);
      if (refusal) {
        // Refused before anything was stamped; the quote is still a draft.
        // `data?.email` is the override that was actually POSTED — undefined
        // when none was, which is the state the forced retry has to reproduce.
        setSuppressed({ refusal, email: data?.email });
        return;
      }
      refuse(err, 'Failed to send quote');
    },
  });

  const [pendingAction, setPendingAction] = useState<PendingQuoteAction | null>(null);

  const acceptMutation = useMutation({
    mutationFn: () => api.quotes.accept(quoteId!),
    onSuccess: (res) => {
      afterAction();
      setToast(acceptedToast(res.data?.binding));
    },
    onError: (err: any) => refuse(err, 'Failed to accept quote'),
  });

  const declineMutation = useMutation({
    mutationFn: () => api.quotes.decline(quoteId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
      setToast('Quote marked as declined');
    },
    onError: (err: any) => {
      refuse(err, 'Failed to decline quote');
    },
  });

  const convertMutation = useMutation({
    mutationFn: () => api.quotes.convert(quoteId!),
    onSuccess: (res: any) => {
      afterAction();
      // The API answers { data: { quote, invoice } }. This read `data.invoiceId`,
      // which the endpoint has never sent, so the jump to the new invoice never
      // happened.
      const invoiceId = res.data?.data?.invoice?.id;
      if (invoiceId) {
        setToast('Quote converted to invoice');
        setTimeout(() => navigate(`/invoices/${invoiceId}`), 1500);
      } else {
        setToast('Quote converted to invoice');
      }
    },
    onError: (err: any) => {
      refuse(err, 'Failed to convert quote');
    },
  });

  const rentalOrderMutation = useMutation({
    mutationFn: () => api.quotes.createRentalOrder(quoteId!, { startDate: rentalStart }),
    onSuccess: (res) => {
      afterAction();
      const d = res.data?.data;
      const unassigned = d?.unassigned?.length ?? 0;
      setToast(d?.alreadyCreated
        ? `Rental order ${d.order.order_number} already exists for this quote`
        : `Rental order ${d?.order.order_number} created as a draft${unassigned ? ` — ${unassigned} line${unassigned === 1 ? '' : 's'} to assign an asset` : ''}`);
    },
    onError: (err: any) => {
      refuse(err, 'Failed to create the rental order');
    },
  });

  const createJobMutation = useMutation({
    mutationFn: () => api.quotes.createJob(quoteId!),
    onSuccess: (res) => {
      afterAction();
      const d = res.data?.data;
      setToast(d?.alreadyCreated
        ? `Job ${d.job.job_number} already exists for this quote`
        : `Job ${d?.job.job_number} created${d?.rateSheet ? `, priced from "${d.rateSheet.name}"` : ''}`);
    },
    onError: (err: any) => refuse(err, 'Failed to create the job'),
  });

  const toolTermsMutation = useMutation({
    mutationFn: (v: { term: QuoteToolTerm; minimumCharge: string; lihRate: string }) =>
      api.quotes.updateToolTerms(quoteId!, v.term.dh_rate_id, {
        minimumCharge: v.minimumCharge.trim() === '' ? null : v.minimumCharge.trim(),
        lihRate: v.lihRate.trim() === '' ? null : v.lihRate.trim(),
      }),
    onSuccess: (_res, v) => {
      afterAction();
      setToast(`Terms saved for ${v.term.label}`);
    },
    onError: (err: any) => refuse(err, 'Failed to save the tool terms'),
  });

  const createPtJobMutation = useMutation({
    mutationFn: () => api.quotes.createPtJob(quoteId!, ptDay ? { scheduledDate: ptDay } : {}),
    onSuccess: (res) => {
      afterAction();
      const d = res.data?.data;
      setToast(d?.alreadyCreated
        ? `PT job ${d.job.job_number} already exists for this quote`
        : `PT job ${d?.job.job_number} scheduled`);
    },
    onError: (err: any) => refuse(err, 'Failed to create the PT job'),
  });

  // An accepted NDT quote → a scheduled NDT job, opened on its own page (api#1166).
  const createNdtJobMutation = useMutation({
    mutationFn: () => api.quotes.createNdtJob(quoteId!, ptDay ? { scheduledDate: ptDay } : {}),
    onSuccess: (res) => {
      afterAction();
      const d = res.data?.data;
      setToast(d?.alreadyCreated
        ? `NDT job ${d.job.jobNumber} already exists for this quote`
        : `NDT job ${d?.job.jobNumber} scheduled`);
      if (d?.job.id) navigate(`/ndt-inspection/jobs/${d.job.id}`);
    },
    onError: (err: any) => refuse(err, 'Failed to create the NDT job'),
  });

  // ── Actions ────────────────────────────────────────────────────────────

  const handleDownloadPdf = async () => {
    try {
      const response = await api.quotes.getPdf(quoteId!);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      setToast('Failed to download PDF');
    }
  };

  // The three status actions ask through the app's ConfirmModal, not the
  // browser's native dialog. Same sentence, same mutation on confirm, nothing
  // on cancel.
  const handleAccept = () => {
    // A downhole accept is binding: say what it writes before it writes it.
    const ask = quote?.vertical === 'downhole_tools'
      ? downholeAcceptQuestion(quote.line_items ?? [])
      : 'Mark this quote as accepted?';
    setPendingAction({ kind: 'accept', body: ask });
  };

  const handleDecline = () => {
    setPendingAction({ kind: 'decline', body: 'Mark this quote as declined?' });
  };

  const handleConvert = () => {
    setPendingAction({ kind: 'convert', body: 'Convert this quote to an invoice? This will create a new invoice with matching line items.' });
  };

  const confirmPendingAction = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (!action) return;
    if (action.kind === 'accept') acceptMutation.mutate();
    else if (action.kind === 'decline') declineMutation.mutate();
    else convertMutation.mutate();
  };

  // ── Three load states, never two ───────────────────────────────────────
  //
  // `isError || !quote` collapsed a REFUSAL, an unreachable server and a row
  // that does not exist into one sentence — "Quote not found" — which names
  // deletion, and is wrong for the first two.

  if (quoteFailure) {
    return (
      <div data-testid="quote-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <QuoteBand title={quoteId ? `Quote ${quoteId.slice(0, 8)}` : 'Quote'} onBack={() => navigate(-1)} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8">
              <LoadFailureNotice
                error={queryFailureError(quoteQuery)}
                /* A PLURAL NOUN PHRASE: the helper renders "not a statement
                   about your {subject}". */
                subject="quote details"
                roleLabel={user?.role ? getRoleLabel(user.role) : undefined}
                allowed="admins and office staff"
              />
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div data-testid="quote-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <QuoteBand title="Loading…" onBack={() => navigate(-1)} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto flex max-w-[1920px] items-center justify-center gap-2 py-20 text-fieldiq-text">
            <Loader2 size={20} className="animate-spin" /> Loading quote…
          </div>
        </section>
      </div>
    );
  }

  // The load finished, the server answered, and it had no quote for this id.
  if (!quote) {
    return (
      <div data-testid="quote-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <QuoteBand title="Quote not found" onBack={() => navigate(-1)} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8 text-center">
              <AlertTriangle size={32} className="mx-auto mb-3 text-fieldiq-muted" aria-hidden="true" />
              <p className="text-sm font-semibold text-white">There is no quote with this id</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-fieldiq-muted">
                It may have been deleted, or the link may be wrong.
              </p>
              <button
                onClick={() => navigate('/quotes')}
                className="fi-action fi-action--standard fi-action--primary mt-4 inline-flex"
              >
                View all quotes
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const status = quote.status;
  const lineItems = quote.line_items ?? [];
  const discountTotal = Number(quote.discount_total || 0);
  // CA$ / US$ on an NDT quote; null (the bare '$', as before) on every other.
  const cur = ndtQuoteCurrency(quote) ?? pfQuoteCurrency(quote) ?? wtQuoteCurrency(quote) ?? ewQuoteCurrency(quote) ?? wsQuoteCurrency(quote);
  const skin = statusSkin(status);
  // Derived from the document's own date, never from the status column. See
  // isPastValidUntil above for what the API does with an expired quote, which
  // is nothing at all.
  const lapsed = status !== 'expired' && isPastValidUntil(quote.valid_until, timeZone);
  const lapsedReason = lapsed
    ? `This quote's valid-until date (${formatDay(quote.valid_until)}) has passed. Edit the quote to extend it before sending.`
    : undefined;

  return (
    <div data-testid="quote-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <QuoteBand
        title={quote.quote_number || `Quote #${quoteId?.slice(0, 8)}`}
        onBack={() => navigate(-1)}
        /* FOUR FIGURES THE QUOTE ITSELF STATES, none recomputed and none a
           second answer to a question the API already answers. Total and
           Subtotal come off the row through `formatMoney`, which is already an
           em dash when the key is absent; Lines is the length of an array this
           screen has in hand and renders below; Valid is the document's own
           date, through the calendar-day helper.

           THE THIRD LABEL IS FIVE CHARACTERS ON PURPOSE. The hero circle's edge
           crosses the third stat column between 1024 and 1048 wide and the
           labels swept there run to about 70px. All four were re-measured for
           this page anyway — no previous sweep covers this copy. */
        stats={[
          {
            icon: <DollarSign className="size-4" aria-hidden="true" />,
            label: 'Total',
            sub: 'Quoted amount',
            tone: 'accent',
            value: formatMoney(quote.total_amount, cur),
          },
          {
            icon: <Receipt className="size-4" aria-hidden="true" />,
            label: 'Subtotal',
            sub: 'Before tax',
            tone: 'neutral',
            value: formatMoney(quote.subtotal, cur),
          },
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Lines',
            sub: 'Quoted items',
            tone: 'info',
            value: displayValue(lineItems.length),
          },
          {
            icon: <Calendar className="size-4" aria-hidden="true" />,
            label: 'Valid',
            sub: 'Until this day',
            tone: 'success',
            value: formatDay(quote.valid_until),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
        {/* ── Identity row — status, the derived lapse, customer ──────────
            In the body rather than on the band, the way TicketDetail's is. */}
        <div data-testid="quote-identity-row" className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span data-testid="quote-status-badge" className={`${STATUS_PILL} ${skin.fill}`}>
            {skin.label}
          </span>
          {/* A SECOND chip, not a replacement for the first. The status column
              says what the API believes; this says what the printed date says.
              Slate, because "past its date" is not a state anyone put it in. */}
          {lapsed && (
            <span
              data-testid="quote-lapsed-badge"
              title={lapsedReason}
              className={`${STATUS_PILL} bg-fi-signal-slate`}
            >
              Expired
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-white">
            <Users size={14} className="text-fieldiq-muted" aria-hidden="true" />
            {quote.customer_name || quote.customer_display_name || '\u2014'}
          </span>
        </div>
        {/* ── Customer Info ───────────────────────────────────────────── */}
        <RecordPanel eyebrow="Quoted to" title={quote.customer_name || quote.customer_display_name || '\u2014'}>
          <div className="px-4 py-3">
          {quote.customer_contact_email ? (
            <>
              <p className="text-sm text-fieldiq-text">{quote.customer_contact_email}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {quote.customer_contact_email_source === 'accounting_email'
                  ? 'Accounting address on file for this customer'
                  : quote.customer_contact_email_source === 'main_contact_email'
                    ? 'Main contact address on file for this customer'
                    : 'Source not recorded'}
              </p>
            </>
          ) : (
            /* ABSENT, said plainly. The panel used to render nothing at all
               here, so "this quote has no recipient" and "the address is
               below the fold" looked the same. */
            <p className="text-sm text-fieldiq-muted">No recipient on this quote</p>
          )}
          {(quote.customer_address || quote.customer_city) && (
            <p className="mt-0.5 text-sm text-fieldiq-text">
              {[quote.customer_address, quote.customer_city, quote.customer_province_state].filter(Boolean).join(', ')}
            </p>
          )}
          </div>
        </RecordPanel>

        {/* ── Converted Invoice Link ──────────────────────────────────── */}
        {quote.converted_invoice_id && (
          /* A LINK, not a clickable div. It was a bare `onClick` on a div:
             no role, no href, unreachable by keyboard and invisible to a
             screen reader, on the one control that takes the reader to the
             invoice this quote became.

             The hue is on the ICON and the EDGE; the words are plain ink. The
             400-weight green they carried is remapped by nothing and reads
             about 1.5 on the white card. */
          <button
            type="button"
            onClick={() => navigate(`/invoices/${quote.converted_invoice_id}`)}
            className="flex w-full items-center justify-between rounded-fi-panel border border-fieldiq-success/30 bg-fieldiq-success/10 p-4 text-left transition-colors hover:border-fieldiq-success/60"
          >
            <span className="flex items-center gap-3">
              <ArrowRightLeft size={18} className="text-fieldiq-success" aria-hidden="true" />
              <span>
                <span className="block text-sm font-semibold text-white">Converted to Invoice</span>
                <span className="mt-0.5 block text-xs text-fieldiq-text">
                  {/* `converted_at` is an INSTANT with an offset, not a
                      calendar day — an absent one is an em dash, never the
                      empty string this rendered. */}
                  {formatDate(quote.converted_at, timeZone)}
                </span>
              </span>
            </span>
            <ExternalLink size={16} className="text-fieldiq-success" aria-hidden="true" />
          </button>
        )}

        {suppressed && (
          <SuppressedRecipientNotice
            refusal={suppressed.refusal}
            documentLabel="quote"
            busy={sendMutation.isPending}
            // Retry the SAME request that was refused, plus the force flag —
            // the override if there was one, and no override if there was not.
            onForce={() => sendMutation.mutate({ email: suppressed.email, force: true })}
            onDismiss={() => setSuppressed(null)}
          />
        )}

        {refusal && (
          <div role="alert" data-testid="quote-action-refusal"
            className="flex items-start justify-between gap-3 rounded-fi-panel border border-fieldiq-danger/40 bg-fieldiq-danger/10 p-4">
            <div>
              <p className="text-sm font-semibold text-white">{refusal.error}</p>
              {refusal.code && <p className="mt-1 font-mono text-xs text-fieldiq-muted">{refusal.code}</p>}
            </div>
            <button onClick={() => setRefusal(null)} aria-label="Dismiss" className="text-fieldiq-muted hover:text-fieldiq-accent">
              <X size={16} />
            </button>
          </div>
        )}

        {/* ── Action Buttons ──────────────────────────────────────────── */}
        {/*
          THE SEND DOORS REFUSE A LAPSED QUOTE, WITH THE REASON.

          What the API does about it: nothing. `POST /quotes/:id/send` never
          reads `valid_until`, and the only writer of the `expired` status —
          `POST /quotes/mark-expired` — has no caller in this app and no
          scheduler in fieldiq-api. So a quote's expiry date passes, the row
          still reads Draft or Sent, and the office mails a price that lapsed.

          Accept and Decline are NOT gated. Recording what the customer said
          about a quote that has since lapsed is a fact about the past, and
          `POST /quotes/:id/accept` takes `sent` or `draft` at any date.
        */}
        {isSales ? (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handleDownloadPdf} className={ACTION_SECONDARY}>
            <Download size={15} />
            Download PDF
          </button>
        </div>
        ) : (
        <div className="flex flex-wrap items-center gap-2">
          {status === 'draft' && (
            <>
              <button onClick={() => navigate(`/quotes/${quoteId}/edit`)} className={ACTION_SECONDARY}>
                <Pencil size={15} />
                Edit
              </button>
              {showSendEmail ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="email"
                    aria-label="Quote recipient"
                    value={sendEmailAddress}
                    onChange={(e) => setSendEmailAddress(e.target.value)}
                    placeholder="Enter customer email"
                    className="w-56 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text placeholder-fieldiq-muted focus:border-fieldiq-accent focus:outline-none"
                    autoFocus
                  />
                  <Tooltip content={sendBlockReason(lapsedReason, sendEmailAddress, quote.customer_contact_email)}>
                    <button
                      onClick={() => {
                        const typed = sendEmailAddress.trim();
                        // Post `email` ONLY when it differs from the quote's own
                        // address. Posting the pre-fill back would make every
                        // send an explicit override, and a refusal would then
                        // lose the line naming which customer field died — see
                        // the twin comment in InvoiceDetailScreen.
                        sendMutation.mutate(!typed || typed === (quote.customer_contact_email || '') ? {} : { email: typed });
                      }}
                      disabled={sendMutation.isPending || !!sendBlockReason(lapsedReason, sendEmailAddress, quote.customer_contact_email)}
                      aria-disabled={!!sendBlockReason(lapsedReason, sendEmailAddress, quote.customer_contact_email)}
                      className={ACTION_PRIMARY}
                    >
                      {sendMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                      Send
                    </button>
                  </Tooltip>
                  <button onClick={() => setShowSendEmail(false)} className="text-fieldiq-muted transition-colors hover:text-fieldiq-accent" aria-label="Cancel">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <Tooltip content={lapsedReason}>
                  <button
                    onClick={() => { setSendEmailAddress(quote.customer_contact_email || ''); setShowSendEmail(true); }}
                    disabled={sendMutation.isPending || lapsed}
                    aria-disabled={lapsed}
                    className={ACTION_PRIMARY}
                  >
                    {sendMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    Send
                  </button>
                </Tooltip>
              )}
            </>
          )}

          {status === 'sent' && (
            <>
              <button onClick={handleAccept} disabled={acceptMutation.isPending} className={ACTION_SECONDARY}>
                {acceptMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                Accept
              </button>
              <button onClick={handleDecline} disabled={declineMutation.isPending} className={ACTION_DANGER}>
                {declineMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />}
                Decline
              </button>
              <button onClick={handleDownloadPdf} className={ACTION_SECONDARY}>
                <Download size={15} />
                PDF
              </button>
              {showSendEmail ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="email"
                    aria-label="Quote recipient"
                    value={sendEmailAddress}
                    onChange={(e) => setSendEmailAddress(e.target.value)}
                    placeholder="Enter customer email"
                    className="w-56 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text placeholder-fieldiq-muted focus:border-fieldiq-accent focus:outline-none"
                    autoFocus
                  />
                  <Tooltip content={sendBlockReason(lapsedReason, sendEmailAddress, quote.customer_contact_email)}>
                    <button
                      onClick={() => {
                        const typed = sendEmailAddress.trim();
                        sendMutation.mutate(!typed || typed === (quote.customer_contact_email || '') ? {} : { email: typed });
                      }}
                      disabled={sendMutation.isPending || !!sendBlockReason(lapsedReason, sendEmailAddress, quote.customer_contact_email)}
                      aria-disabled={!!sendBlockReason(lapsedReason, sendEmailAddress, quote.customer_contact_email)}
                      className={ACTION_SECONDARY}
                    >
                      {sendMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                      Resend
                    </button>
                  </Tooltip>
                  <button onClick={() => setShowSendEmail(false)} className="text-fieldiq-muted transition-colors hover:text-fieldiq-accent" aria-label="Cancel">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <Tooltip content={lapsedReason}>
                  <button
                    onClick={() => { setSendEmailAddress(quote.customer_contact_email || ''); setShowSendEmail(true); }}
                    disabled={sendMutation.isPending || lapsed}
                    aria-disabled={lapsed}
                    className={ACTION_SECONDARY}
                  >
                    {sendMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    Resend
                  </button>
                </Tooltip>
              )}
            </>
          )}

          {status === 'accepted' && quote.vertical === 'surface_rentals' && (
            <span className="inline-flex items-center gap-2" data-testid="create-rental-order">
              <input
                type="date"
                aria-label="Rental start date"
                value={rentalStart}
                onChange={(e) => setRentalStart(e.target.value)}
                className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white"
              />
              <button
                onClick={() => rentalOrderMutation.mutate()}
                disabled={!rentalStart || rentalOrderMutation.isPending}
                className={ACTION_PRIMARY}
              >
                {rentalOrderMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <ArrowRightLeft size={15} />}
                Create Rental Order
              </button>
            </span>
          )}

          {/* A casing quote converts straight to an invoice; every other vertical
              bills through its own records (api QUOTE_CONVERT_CASING_ONLY). */}
          {/* A casing quote can ALSO become a job, pinned to its rate sheet
              (api#957) — one road per quote: a quote that became a job will
              not convert, and a converted one will not become a job. */}
          {status === 'accepted' && !quote.converted_invoice_id && (quote.vertical ?? 'casing_running') === 'casing_running'
            && !(created?.jobs.length) && (
            <button onClick={() => createJobMutation.mutate()} disabled={createJobMutation.isPending} className={ACTION_PRIMARY}>
              {createJobMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
              Create Job
            </button>
          )}

          {status === 'accepted' && quote.vertical === 'pressure_testing' && !(created?.ptJobs.length) && (
            <span className="inline-flex items-center gap-2" data-testid="create-pt-job">
              <input
                type="date"
                aria-label="PT job day (optional)"
                value={ptDay}
                onChange={(e) => setPtDay(e.target.value)}
                className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white"
              />
              <button onClick={() => createPtJobMutation.mutate()} disabled={createPtJobMutation.isPending} className={ACTION_PRIMARY}>
                {createPtJobMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                Create PT Job
              </button>
            </span>
          )}

          {status === 'accepted' && quote.vertical === 'ndt_inspection' && created && !(created.ndtJobs?.length) && (
            <span className="inline-flex items-center gap-2" data-testid="create-ndt-job">
              <input
                type="date"
                aria-label="NDT job day (optional)"
                value={ptDay}
                onChange={(e) => setPtDay(e.target.value)}
                className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white"
              />
              <button onClick={() => createNdtJobMutation.mutate()} disabled={createNdtJobMutation.isPending} className={ACTION_PRIMARY}>
                {createNdtJobMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
                Create NDT Job
              </button>
            </span>
          )}

          {/* An accepted Pipeline quote → a Pipeline job on a picked site (api#1232).
              admin / office staff only — the door's requireRole — and only once
              created-records says it has none (absent pfJobs = not known yet). */}
          {status === 'accepted' && quote.vertical === PF_QUOTE_VERTICAL && canAdminPf(user?.role)
            && created && Array.isArray(created.pfJobs) && created.pfJobs.length === 0 && (
            <CreatePfJobAction
              quoteId={quoteId!}
              customerId={quote.customer_id}
              onCreated={(d) => {
                afterAction();
                queryClient.invalidateQueries({ queryKey: ['pipelineFacility'] });
                setToast(d.alreadyCreated
                  ? `Pipeline job ${d.job.jobNumber} already exists for this quote`
                  : `Pipeline job ${d.job.jobNumber} scheduled`);
                navigate(`/pipeline-facility/jobs/${d.job.id}`);
              }}
              onRefused={setRefusal}
            />
          )}

          {/* An accepted Well Testing quote → a Well Testing job on a picked well (api#1258).
              admin / office staff only — the door's requireRole — and only once
              created-records says it has none (absent wtJobs = not known yet). */}
          {status === 'accepted' && quote.vertical === WT_QUOTE_VERTICAL && canAdminWt(user?.role)
            && created && Array.isArray(created.wtJobs) && created.wtJobs.length === 0 && (
            <CreateWtJobAction
              quoteId={quoteId!}
              customerId={quote.customer_id}
              onCreated={(d) => {
                afterAction();
                queryClient.invalidateQueries({ queryKey: ['wellTesting'] });
                setToast(d.alreadyCreated
                  ? `Well Testing job ${d.job.jobNumber} already exists for this quote`
                  : `Well Testing job ${d.job.jobNumber} scheduled`);
                navigate(`/well-testing/jobs/${d.job.id}`);
              }}
              onRefused={setRefusal}
            />
          )}

          {/* An accepted Earthworks quote → an Earthworks job on a picked site, with the
              quote's lines (EW-2). admin / office staff only, and only once
              created-records says it has none (absent ewJobs = not known yet). */}
          {status === 'accepted' && quote.vertical === EW_QUOTE_VERTICAL && canAdminEw(user?.role)
            && created && Array.isArray(created.ewJobs) && created.ewJobs.length === 0 && (
            <CreateEwJobAction
              quoteId={quoteId!}
              customerId={quote.customer_id}
              onCreated={(d) => {
                afterAction();
                queryClient.invalidateQueries({ queryKey: ['earthworks'] });
                setToast(d.alreadyCreated
                  ? `Earthworks job ${d.job.jobNumber} already exists for this quote`
                  : `Earthworks job ${d.job.jobNumber} created`);
                navigate(`/earthworks/jobs/${d.job.id}`);
              }}
              onRefused={setRefusal}
            />
          )}

          {/* An accepted Well Servicing quote → a Well Servicing job on a picked well,
              with its scopes (WS-5, api#1295). admin / office staff only — the
              door's requireRole — and only once created-records says it has none
              (absent wsJobs = not known yet). */}
          {status === 'accepted' && quote.vertical === WS_QUOTE_VERTICAL && canAdminWs(user?.role)
            && created && Array.isArray(created.wsJobs) && created.wsJobs.length === 0 && (
            <CreateWsJobAction
              quoteId={quoteId!}
              customerId={quote.customer_id}
              onCreated={(d) => {
                afterAction();
                queryClient.invalidateQueries({ queryKey: ['wellServicing'] });
                setToast(d.alreadyCreated
                  ? `Well Servicing job ${d.job.jobNumber} already exists for this quote`
                  : `Well Servicing job ${d.job.jobNumber} scheduled`);
                navigate(`/well-servicing/jobs/${d.job.id}`);
              }}
              onRefused={setRefusal}
            />
          )}

          {/* Earthworks "Convert to invoice" is DESIGNED, not built: Batch 6 lands it
              in the ONE shared Invoices list on the ONE company invoice number
              series (docs/verticals/earthworks/DESIGN.md in fieldiq-api, Batch 6). */}
          {status === 'accepted' && quote.vertical === EW_QUOTE_VERTICAL && (
            <span title="Coming in Earthworks Batch 6: the invoice lands in the shared Invoices list, on the company's one invoice number series.">
              <button type="button" disabled className={ACTION_SECONDARY} data-testid="ew-convert-invoice-disabled">
                <ArrowRightLeft size={15} />
                Convert to Invoice (coming soon)
              </button>
            </span>
          )}

          {status === 'accepted' && !quote.converted_invoice_id && (quote.vertical ?? 'casing_running') === 'casing_running'
            && !(created?.jobs.length) && (
            <button onClick={handleConvert} disabled={convertMutation.isPending} className={ACTION_PRIMARY}>
              {convertMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <ArrowRightLeft size={15} />}
              Convert to Invoice
            </button>
          )}

          {/* The PDF is a READ and is available on every status. It used to be
              absent from the draft branch, so the one document a draft quote
              can produce was unreachable from the screen that shows it. */}
          {status !== 'sent' && (
            <button onClick={handleDownloadPdf} className={ACTION_SECONDARY}>
              <Download size={15} />
              Download PDF
            </button>
          )}
        </div>
        )}

        {quote.vertical === 'downhole_tools' && (
          <QuoteToolTermsPanel
            terms={quote.tool_terms ?? []}
            currency={quote.currency || 'CAD'}
            editable={status === 'draft' && !isSales}
            savingId={toolTermsMutation.isPending ? toolTermsMutation.variables?.term.dh_rate_id ?? null : null}
            onSave={(term, minimumCharge, lihRate) => toolTermsMutation.mutate({ term, minimumCharge, lihRate })}
          />
        )}

        {quote.vertical === 'downhole_tools' && status !== 'accepted' && (
          <QuoteCrewBindingPanel lines={quote.line_items ?? []} />
        )}

        {quote.vertical === EW_QUOTE_VERTICAL && (
          <EwQuotePanel
            quoteId={quoteId!}
            canEdit={canAdminEw(user?.role)}
            timeZone={timeZone}
            onRefused={setRefusal}
            onToast={setToast}
          />
        )}

        {created && <QuoteCreatedRecordsPanel records={created} />}

        {/* ── Quote Details ───────────────────────────────────────────── */}
        {/*
          THE FOUR `_at` COLUMNS ARE INSTANTS, and they were rendered in raw
          400-weight blue, green and red — hues remapped by nothing, which read
          about 1.7-2.5 on the white card in light mode. The hue said what the
          status badge two panels up already says, in a channel half the
          installs cannot read. Plain heading ink; the LABEL names the event.
        */}
        <RecordPanel eyebrow="Dates on this quote" title="Timeline">
          <dl className="grid grid-cols-2 gap-3 px-4 py-3 text-sm">
            <div>
              <dt className="text-fieldiq-text">Quote Date</dt>
              <dd className="font-medium text-white">{formatInstantTenantDay(quote.quote_date || quote.created_at, timeZone) ?? '\u2014'}</dd>
            </div>
            <div>
              <dt className="text-fieldiq-text">Valid Until</dt>
              <dd className="font-medium text-white">{formatDay(quote.valid_until)}</dd>
            </div>
            {quote.sent_at && (
              <div>
                <dt className="text-fieldiq-text">Sent</dt>
                <dd className="font-medium text-white">{formatDate(quote.sent_at, timeZone)}</dd>
              </div>
            )}
            {quote.accepted_at && (
              <div>
                <dt className="text-fieldiq-text">Accepted</dt>
                <dd className="font-medium text-white">{formatDate(quote.accepted_at, timeZone)}</dd>
              </div>
            )}
            {quote.declined_at && (
              <div>
                <dt className="text-fieldiq-text">Declined</dt>
                <dd className="font-medium text-white">{formatDate(quote.declined_at, timeZone)}</dd>
              </div>
            )}
          </dl>
        </RecordPanel>

        {/* ── Notes ───────────────────────────────────────────────────── */}
        {quote.notes && (
          <RecordPanel eyebrow="On the document" title="Notes">
            <p className="whitespace-pre-wrap px-4 py-3 text-sm text-fieldiq-text">{quote.notes}</p>
          </RecordPanel>
        )}

        {/* ── Line Items Table ────────────────────────────────────────── */}
        <RecordPanel eyebrow="What was quoted" title="Line items" count={lineItems.length}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-fieldiq-text text-left">
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="px-4 py-2 font-medium text-right">Qty</th>
                  <th className="px-4 py-2 font-medium">Unit</th>
                  <th className="px-4 py-2 font-medium text-right">Rate</th>
                  <th className="px-4 py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-fieldiq-text">
                      No line items
                    </td>
                  </tr>
                ) : (
                  lineItems.map((item) => (
                    /* A DISCOUNT ROW IS ITALIC, NOT RED. The 400-weight red it
                       carried is remapped by nothing and reads about 2.5 on the
                       white card, so on half the installs the discount lines
                       were the least readable rows on the quote. The italic and
                       the leading minus carry it. */
                    <tr
                      key={item.id}
                      className={`border-b border-fieldiq-border/50 ${
                        item.is_discount ? 'italic text-fieldiq-text' : 'text-white'
                      }`}
                    >
                      {/* THE COLUMN VALUES, NOT THE RAW ENUMS. This printed
                          `service` and `per_metre` straight out of the row —
                          while the PDF this very screen attaches to the send
                          renders "Service" and "Per Meter", because the API's
                          own report.service.ts runs both through
                          formatLineItemType / its unit map. One document, two
                          spellings, and the one the customer receives is the
                          PDF. `fmtLineItemType` is this repo's mirror of that
                          function (it also gets `crt` → "CRT", which a bare
                          capitalize renders "Crt"); `fmtUnit` is the unit half.

                          KNOWN REMAINING DIVERGENCE, reported not fixed: the
                          API's map spells `per_metre` "Per Meter" and ours
                          spells it "Per Metre". One letter, on the API's side
                          of the wire — fixing it here would be the second
                          answer to one question. */}
                      <td className="px-4 py-2">{ndtQuoteLineKind(item) ?? pfQuoteLineKind(item) ?? wtQuoteLineKind(item) ?? ewQuoteLineKind(item) ?? wsQuoteLineKind(item) ?? (item.line_item_type ? fmtLineItemType(item.line_item_type) : '\u2014')}</td>
                      <td className="px-4 py-2 max-w-[200px] truncate">{item.description || '\u2014'}</td>
                      <td className="px-4 py-2 text-right">{item.quantity}</td>
                      <td className="px-4 py-2">{item.unit ? fmtUnit(item.unit) : '\u2014'}</td>
                      <td className="px-4 py-2 text-right">{formatRate(item.rate, cur)}</td>
                      <td className="px-4 py-2 text-right font-medium">
                        {item.is_discount ? formatMoneyWithSign(-Math.abs(Number(item.total)), cur) : formatMoney(item.total, cur)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Totals footer ──────────────────────────────────────────── */}
          <div className="border-t border-fieldiq-border px-4 py-3 space-y-1">
            <div className="flex justify-between text-sm text-fieldiq-text">
              <span>Subtotal</span>
              <span className="text-white">{formatMoney(quote.subtotal, cur)}</span>
            </div>

            {discountTotal !== 0 && (
              <div className="flex justify-between text-sm italic text-fieldiq-text">
                <span>Discounts</span>
                <span>-{formatMoney(Math.abs(discountTotal), cur)}</span>
              </div>
            )}

            {quote.tax_rate_1_amount && Number(quote.tax_rate_1_amount) !== 0 && (
              <div className="flex justify-between text-sm text-fieldiq-text">
                <span>
                  {quote.tax_rate_1_label || 'Tax 1'} ({quote.tax_rate_1_percent || 0}%)
                </span>
                <span className="text-white">{formatMoney(quote.tax_rate_1_amount, cur)}</span>
              </div>
            )}

            {quote.tax_rate_2_amount && Number(quote.tax_rate_2_amount) !== 0 && (
              <div className="flex justify-between text-sm text-fieldiq-text">
                <span>
                  {quote.tax_rate_2_label || 'Tax 2'} ({quote.tax_rate_2_percent || 0}%)
                </span>
                <span className="text-white">{formatMoney(quote.tax_rate_2_amount, cur)}</span>
              </div>
            )}

            {/* PLAIN HEADING INK. Weight and the rule above it are the
                hierarchy; a hue here would be a second channel restating the
                status badge, and the accent's meaning on this register is
                "the office must act". */}
            <div className="flex justify-between border-t border-fieldiq-border/50 pt-1 text-base font-semibold text-white">
              <span>Total</span>
              <span>{formatMoney(quote.total_amount, cur)}</span>
            </div>
          </div>
        </RecordPanel>

        {/* ── Audit Log (collapsible) — not drawn for sales, whose read is refused ── */}
        {!isSales && (
        <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
          <button
            onClick={() => setAuditOpen(!auditOpen)}
            aria-expanded={auditOpen}
            aria-controls="quote-audit-log"
            className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:text-fieldiq-accent"
          >
            <span>
              <span className="block text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                Who changed what
              </span>
              <span className="mt-1 flex items-center gap-2 text-base font-extrabold tracking-tight text-white">
                <Clock size={15} className="text-fieldiq-muted" aria-hidden="true" />
                Audit log
              </span>
            </span>
            {auditOpen ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
          </button>

          {auditOpen && (
            <div
              id="quote-audit-log"
              /* Named, so a reader on a screen reader lands somewhere and a
                 test can ask for it by role rather than reach for it by id. */
              role="region"
              aria-label="Audit log entries"
              className="space-y-2 border-t border-fieldiq-border px-4 py-3"
            >
              {auditLoading ? (
                <div className="flex items-center gap-2 text-fieldiq-text text-sm py-4 justify-center">
                  <Loader2 size={16} className="animate-spin" />
                  Loading audit log...
                </div>
              ) : !auditLog || auditLog.length === 0 ? (
                <p className="text-sm text-fieldiq-text text-center py-4">No audit entries</p>
              ) : (
                auditLog.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 text-sm bg-fieldiq-dark/50 rounded-lg px-3 py-2"
                  >
                    <User size={14} className="text-fieldiq-text mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* The raw column value read `quote_sent` on screen. */}
                        <span className="font-medium text-white">{humanizeLabel(entry.action)}</span>
                        {auditActor(entry) && (
                          <span className="text-fieldiq-text">by {auditActor(entry)}</span>
                        )}
                      </div>
                      {entry.description && (
                        <p className="mt-0.5 text-fieldiq-text">{entry.description}</p>
                      )}
                      {/* `created_at` arrives in the POSTGRES shape —
                          '2026-09-04 00:56:18.511471+00' — which V8 parses
                          leniently and JavaScriptCore REJECTS, so a bare
                          `new Date()` here reads correctly on desktop and
                          degrades to "Invalid Date" on the iOS build.
                          parseInstantDate normalises it first; an absent or
                          unparseable stamp is an em dash, never the words. */}
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {formatInstant(entry.created_at, timeZone, {
                          month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                        }) ?? '\u2014'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
        )}
        </div>
      </section>

      {/* ── Accept / Decline / Convert confirmation ───────────────────── */}
      <ConfirmModal
        isOpen={pendingAction !== null}
        title={pendingAction ? PENDING_ACTION_COPY[pendingAction.kind].title : ''}
        body={pendingAction?.body ?? ''}
        confirmLabel={pendingAction ? PENDING_ACTION_COPY[pendingAction.kind].confirmLabel : ''}
        confirmVariant={pendingAction ? PENDING_ACTION_COPY[pendingAction.kind].variant : 'primary'}
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />

      {/* ── Toast ─────────────────────────────────────────────────────── */}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
